// Details-dialog data hook for one saved MCO plan. Location/crop are supplied as props —
// the detail route already has state/county/commodity/type/practice from useParams, so
// (unlike tilley-calculators/src/hooks/useMcoCalculator.js) there is no dropdown cascade.

import { useCallback, useEffect, useState } from "react";

import { MCOService } from "components/Insurance/MCOService";

import { mcoPlanCodeFromName } from "./isMcoPlan.js";
import { loadMcoAreaBundle, mcoTypeCandidates } from "./mcoAreaInputs.js";
import { BACKTEST_VINTAGES } from "./mcoBacktest.js";
import { buildMcoEcoBacktestReport } from "./mcoEcoBacktest.js";
import { ecoParamsFromMirrorRow } from "./mcoEcoScenario.js";
import { computeMcoProjectedPayment } from "./mcoModel.js";
import { loadMcoAreaLossBands, loadMcoLedgerBundle, paymentBaseFromActuarial } from "./mcoOfficialData.js";
import { buildActuarialLedgerRows, buildPaymentFormulaRows } from "./mcoPaymentLedger.js";
import { computeMcoProducerPremium } from "./mcoPremium.js";
import { resolveMcoRmaCodes } from "./mcoRmaCodes.js";
import { buildWattsSalesTracker } from "./mcoSalesTracker.js";
import { num } from "./mcoShared.js";
import { buildMcoStressGrid } from "./mcoStressGrid.js";
import { buildMcoTrackerReport } from "./mcoTracker.js";

export { BACKTEST_VINTAGES };

const mcoService = MCOService().actions;

/**
 * @param {object} opts
 * @param {object} opts.geo — { reinsurance_year, state_name, county_name, commodity_name, type_name, practice_name }
 * @param {object} opts.mcoPlan — the saved MCO insurancePlan ({ insurance_plan_code, coverage_level_percent, ... })
 * @param {object} opts.farm — { approvedYield, acres, sharePct }
 * @param {number} [opts.underlyingCoverage=0.75] — base RP/YP coverage; unused by the comparison tab
 * @param {number} [opts.costMult=1] — stress grid cost-basket multiplier (input-cost $/ac ÷ baseline)
 * @param {object} [opts.gridAxes] — { yieldSteps, priceSteps } to share the Scenario page's
 *   stress-test axes (Rogers: MCO grid must be directly comparable to the RP/SCO grids)
 * @returns {{
 *   isAvailable: boolean, loading: boolean, bands: object[], actuarial: object|null,
 *   priceLegs: object[], paymentLedger: object|null, premium: object|null, projected: object|null,
 *   stressGrid: object|null, stressBaseline: object|null, backtest: object|null, backtestVintage: string,
 *   setBacktestVintage: (v: string) => void, priceTrack: object|null, ecoBaseline: object|null,
 *   blockers: string[],
 * }}
 */
export function useMco({
  geo,
  mcoPlan,
  farm,
  underlyingCoverage = 0.75,
  underlyingPremiumPerAcre,
  underlyingLiabilityPerAcre,
  costMult = 1,
  gridAxes = null,
}) {
  // Saved rows only carry the plan name — dbo.InsurancePlan has no insurance_plan_code column.
  const planCode = String(mcoPlan?.insurance_plan_code || mcoPlanCodeFromName(mcoPlan?.insurance_plan_name) || "");
  const coverageStart = num(mcoPlan?.coverage_level_percent ?? 0.95);
  const approvedYield = num(farm?.approvedYield);
  const acres = num(farm?.acres);
  const sharePct = farm?.sharePct;

  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [bands, setBands] = useState([]);
  const [actuarial, setActuarial] = useState(null);
  const [priceLegs, setPriceLegs] = useState([]);
  const [paymentLedger, setPaymentLedger] = useState(null);
  const [premium, setPremium] = useState(null);
  const [projected, setProjected] = useState(null);
  const [stressBaseline, setStressBaseline] = useState(null);
  const [stressGrid, setStressGrid] = useState(null);
  const [priceTrack, setPriceTrack] = useState(null);
  const [ecoBaseline, setEcoBaseline] = useState(null);
  const [blockers, setBlockers] = useState([]);
  const [backtest, setBacktest] = useState(null);
  const [backtestVintage, setBacktestVintage] = useState(BACKTEST_VINTAGES[0].code);
  const [tracker, setTracker] = useState(null);

  const canLoad = Boolean(
    geo?.reinsurance_year &&
    geo?.state_name &&
    geo?.county_name &&
    geo?.commodity_name &&
    planCode &&
    approvedYield > 0 &&
    acres > 0
  );

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setBlockers([]);
    try {
      const bandRows = await loadMcoAreaLossBands(mcoService, {
        crop_year: geo.reinsurance_year,
        commodity_name: geo.commodity_name,
        state_name: geo.state_name,
        county_name: geo.county_name,
      });
      setBands(bandRows);
      const band = bandRows.find((b) => b.start === coverageStart) || bandRows[0];
      if (!band) {
        setIsAvailable(false);
        setBlockers(["No MCO AreaLoss bands on sale for this commodity/year"]);
        return;
      }
      setIsAvailable(true);

      const common = {
        crop_year: geo.reinsurance_year,
        state_name: geo.state_name,
        county_name: geo.county_name,
        commodity_name: geo.commodity_name,
        practice_name: geo.practice_name,
        insurance_plan_code: planCode,
        approved_yield: approvedYield,
        area_loss_order: band.order,
        area_loss_start_percent: band.start,
        area_loss_end_percent: band.end,
        protection_factor: 1,
      };

      // Try crop type first, then "No Type Specified" (Corn/Soy/Cotton MCO ADM shape).
      let act = null;
      let legs = [];
      let cropPd = null;
      let resolvedType = geo.type_name;
      let ledgerErr = null;
      for (const type_name of mcoTypeCandidates(geo.type_name)) {
        try {
          const ledger = await loadMcoLedgerBundle(mcoService, { ...common, type_name });
          if (ledger?.actuarial && Object.keys(ledger.actuarial).length) {
            act = ledger.actuarial;
            legs = ledger.legs;
            cropPd = ledger.cropPd;
            resolvedType = type_name;
            ledgerErr = null;
            break;
          }
        } catch (e) {
          ledgerErr = e;
        }
      }
      if (!act) {
        setIsAvailable(false);
        console.error("MCO ledger load failed", ledgerErr);
        setBlockers(["Could not load MCO actuarial data"]);
        return;
      }
      setActuarial(act);
      setPriceLegs(legs);
      setPriceTrack(buildWattsSalesTracker(cropPd));

      const paymentBase = paymentBaseFromActuarial(act, legs[0], {
        approvedYield,
        planCode,
        coverageStart: band.start,
        areaLossEnd: band.end,
        protectionFactor: 1,
        sharePct,
        acres,
      });
      // ECO band/price/trigger from dbo.eco_parameters — independent of MCO's legs.
      let eco = null;
      let ecoBlocker = null;
      try {
        const codes = await resolveMcoRmaCodes({ ...geo, crop_year: geo.reinsurance_year });
        if (codes.state_code && codes.county_code && codes.commodity_code) {
          const ecoRows = await mcoService.selectMirror("EcoParameters", {
            state_code: codes.state_code,
            county_code: codes.county_code,
            commodity_code: codes.commodity_code,
            crop_year: geo.reinsurance_year,
            practice_name: geo.practice_name,
            insurance_plan_code: planCode,
            coverage_level_percent: String(band.start ?? 0.95),
          });
          eco = ecoParamsFromMirrorRow(ecoRows?.[0]);
          if (!eco) {
            ecoBlocker = ecoRows?.[0]
              ? "ECO parameters incomplete"
              : "no comparable ECO offer for this county";
          }
        } else {
          ecoBlocker = "ECO parameters missing — RMA codes unresolved";
        }
      } catch (e) {
        console.error("ECO parameters load failed", e);
        ecoBlocker = "ECO parameters unavailable — Mirror EcoParameters not served";
      }
      paymentBase.eco = eco;
      paymentBase.ecoBlocker = ecoBlocker;
      setStressBaseline(paymentBase);
      setEcoBaseline(eco);

      // Projected = "as of today" — expected yield / projected price stand in for final/harvest.
      const projInput = {
        ...paymentBase,
        finalYield: paymentBase.expectedYield,
        harvestPrice: paymentBase.projectedPrice,
      };
      const proj = computeMcoProjectedPayment(projInput);
      setProjected(proj);
      setPaymentLedger({
        formula: buildPaymentFormulaRows(projInput, proj.result),
        actuarial: buildActuarialLedgerRows(act),
        legs,
      });

      const crop = {
        stateName: geo.state_name,
        countyName: geo.county_name,
        commodityName: geo.commodity_name,
        type_name: resolvedType || geo.type_name,
        practice_name: geo.practice_name,
        acres,
        yieldPerAcre: approvedYield,
        sharePct,
        price: paymentBase.projectedPrice,
      };
      const areaBundle = await loadMcoAreaBundle(
        crop,
        {
          planCode,
          coverageStart: band.start,
          areaLossEnd: band.end,
          protectionFactor: 1,
          officialSubsidyPercent: num(act?.subsidyrate),
        },
        geo.reinsurance_year
      );
      setPremium(
        areaBundle.ready
          ? computeMcoProducerPremium(areaBundle.bundle)
          : { ready: false, blockers: areaBundle.blockers, result: null }
      );
    } catch (e) {
      console.error("MCO load failed", e);
      setBlockers(["Could not load MCO data"]);
    } finally {
      setLoading(false);
    }
  }, [
    canLoad,
    geo,
    planCode,
    coverageStart,
    approvedYield,
    acres,
    sharePct,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!stressBaseline) return;
    setStressGrid(
      buildMcoStressGrid(stressBaseline, {
        costMult,
        yieldSteps: gridAxes?.yieldSteps,
        priceSteps: gridAxes?.priceSteps,
      })
    );
  }, [stressBaseline, costMult, gridAxes]);

  const refreshBacktest = useCallback(
    async (vintageOverride) => {
      const vintage = vintageOverride || backtestVintage;
      if (vintageOverride) setBacktestVintage(vintageOverride);
      if (!canLoad) return;
      try {
        const codes = await resolveMcoRmaCodes({ ...geo, crop_year: geo.reinsurance_year });
        if (!codes.commodity_code) return;
        const mirrorParams = {
          state_code: codes.state_code,
          county_code: codes.county_code,
          commodity_code: codes.commodity_code,
          practice_name: geo.practice_name,
        };
        const [mcoRows, ecoRows] = await Promise.all([
          mcoService.selectMirror("BacktestMargins", {
            ...mirrorParams,
            insurance_plan_code: planCode,
            endorsement_vintage: vintage,
          }),
          // eco_backtest_comparison carries no endorsement_vintage — it's keyed to whichever
          // vintage is_default_vintage (payment_spec.md 4.3); it fails soft into a blocker
          // per year via ecoIndemnityForBacktestYear, never blocking the MCO side.
          mcoService
            .selectMirror("EcoBacktestComparison", { ...mirrorParams, mco_insurance_plan_code: planCode })
            .catch((e) => {
              console.error("MCO eco backtest comparison load failed", e);
              return [];
            }),
        ]);
        setBacktest(
          buildMcoEcoBacktestReport(
            mcoRows,
            ecoRows,
            { approvedYield, protectionFactor: 1, sharePct, acres, planCode },
            geo.practice_name
          )
        );
      } catch (e) {
        console.error("MCO backtest load failed", e);
        setBacktest({ ready: false, blockers: ["Could not load backtest"], years: [], disclaimer: null });
      }
    },
    [canLoad, geo, planCode, approvedYield, sharePct, acres, backtestVintage]
  );

  useEffect(() => {
    refreshBacktest();
  }, [canLoad, geo, planCode]);

  const refreshTracker = useCallback(async () => {
    if (!canLoad) return;
    try {
      const codes = await resolveMcoRmaCodes({ ...geo, crop_year: geo.reinsurance_year });
      if (!codes.state_code) return;
      const rows = await mcoService.selectMirror("TrackerDaily", {
        state_code: codes.state_code,
        commodity_name: geo.commodity_name,
        practice_name: geo.practice_name,
        crop_year: geo.reinsurance_year,
      });
      setTracker(
        buildMcoTrackerReport(rows, {
          eay: stressBaseline?.expectedYield,
          eayBasis: stressBaseline?.expectedYieldBasis || "official",
          reinsuranceYear: geo.reinsurance_year,
          commodityName: geo.commodity_name,
          practiceName: geo.practice_name,
        })
      );
    } catch (e) {
      console.error("MCO tracker load failed", e);
      setTracker({
        ready: false,
        blockers: ["Could not load discovery tracker"],
        series: [],
        windowSeries: [],
        latest: null,
      });
    }
  }, [canLoad, geo, stressBaseline?.expectedYield]);

  useEffect(() => {
    refreshTracker();
  }, [refreshTracker]);

  return {
    isAvailable,
    loading,
    bands,
    actuarial,
    priceLegs,
    paymentLedger,
    premium,
    projected,
    stressGrid,
    stressBaseline,
    backtest,
    backtestVintage,
    setBacktestVintage: refreshBacktest,
    priceTrack,
    tracker,
    ecoBaseline,
    blockers,
  };
}
